import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Download, TrendingUp, BarChart3, Activity, BarChart } from 'lucide-react';
import { batchService } from '../services/batchService';

// Breed Standards Configuration
interface BreedStandard {
  breed: string;
  type: 'broiler' | 'layer';
  weightTargets: { age: number; weight: number }[];
  fcrTargets: { age: number; fcr: number }[];
  mortalityTargets: { age: number; mortality: number }[];
  eggProduction?: { age: number; eggsPerBird: number }[];
}

const BREED_STANDARDS: BreedStandard[] = [
  {
    breed: 'COBB 500',
    type: 'broiler',
    weightTargets: [
      { age: 7, weight: 0.18 },
      { age: 14, weight: 0.45 },
      { age: 21, weight: 0.85 },
      { age: 28, weight: 1.35 },
      { age: 35, weight: 1.95 },
      { age: 42, weight: 2.65 }
    ],
    fcrTargets: [
      { age: 7, fcr: 1.2 },
      { age: 14, fcr: 1.35 },
      { age: 21, fcr: 1.45 },
      { age: 28, fcr: 1.55 },
      { age: 35, fcr: 1.65 },
      { age: 42, fcr: 1.75 }
    ],
    mortalityTargets: [
      { age: 7, mortality: 1.0 },
      { age: 14, mortality: 1.5 },
      { age: 21, mortality: 2.0 },
      { age: 28, mortality: 2.5 },
      { age: 35, mortality: 3.0 },
      { age: 42, mortality: 3.5 }
    ]
  },
  {
    breed: 'Hy-Line Brown',
    type: 'layer',
    weightTargets: [
      { age: 6, weight: 0.45 },
      { age: 8, weight: 0.60 },
      { age: 10, weight: 0.75 },
      { age: 12, weight: 0.90 },
      { age: 14, weight: 1.05 },
      { age: 16, weight: 1.20 },
      { age: 18, weight: 1.35 },
      { age: 20, weight: 1.50 },
      { age: 22, weight: 1.60 },
      { age: 24, weight: 1.70 },
      { age: 26, weight: 1.75 },
      { age: 28, weight: 1.80 },
      { age: 30, weight: 1.85 },
      { age: 32, weight: 1.90 },
      { age: 34, weight: 1.95 },
      { age: 36, weight: 2.00 },
      { age: 40, weight: 2.05 },
      { age: 44, weight: 2.10 },
      { age: 48, weight: 2.15 },
      { age: 52, weight: 2.20 },
      { age: 56, weight: 2.25 },
      { age: 60, weight: 2.30 },
      { age: 64, weight: 2.35 },
      { age: 68, weight: 2.40 },
      { age: 72, weight: 2.45 }
    ],
    fcrTargets: [
      { age: 6, fcr: 1.8 },
      { age: 8, fcr: 1.9 },
      { age: 10, fcr: 2.0 },
      { age: 12, fcr: 2.1 },
      { age: 14, fcr: 2.2 },
      { age: 16, fcr: 2.3 },
      { age: 18, fcr: 2.4 },
      { age: 20, fcr: 2.5 },
      { age: 22, fcr: 2.6 },
      { age: 24, fcr: 2.7 },
      { age: 26, fcr: 2.8 },
      { age: 28, fcr: 2.9 },
      { age: 30, fcr: 3.0 },
      { age: 32, fcr: 3.1 },
      { age: 34, fcr: 3.2 },
      { age: 36, fcr: 3.3 },
      { age: 40, fcr: 3.4 },
      { age: 44, fcr: 3.5 },
      { age: 48, fcr: 3.6 },
      { age: 52, fcr: 3.7 },
      { age: 56, fcr: 3.8 },
      { age: 60, fcr: 3.9 },
      { age: 64, fcr: 4.0 },
      { age: 68, fcr: 4.1 },
      { age: 72, fcr: 4.2 }
    ],
    mortalityTargets: [
      { age: 6, mortality: 0.5 },
      { age: 8, mortality: 0.7 },
      { age: 10, mortality: 0.9 },
      { age: 12, mortality: 1.1 },
      { age: 14, mortality: 1.3 },
      { age: 16, mortality: 1.5 },
      { age: 18, mortality: 1.7 },
      { age: 20, mortality: 1.9 },
      { age: 22, mortality: 2.1 },
      { age: 24, mortality: 2.3 },
      { age: 26, mortality: 2.5 },
      { age: 28, mortality: 2.7 },
      { age: 30, mortality: 2.9 },
      { age: 32, mortality: 3.1 },
      { age: 34, mortality: 3.3 },
      { age: 36, mortality: 3.5 },
      { age: 40, mortality: 3.7 },
      { age: 44, mortality: 3.9 },
      { age: 48, mortality: 4.1 },
      { age: 52, mortality: 4.3 },
      { age: 56, mortality: 4.5 },
      { age: 60, mortality: 4.7 },
      { age: 64, mortality: 4.9 },
      { age: 68, mortality: 5.1 },
      { age: 72, mortality: 5.3 }
    ],
    eggProduction: [
      { age: 18, eggsPerBird: 0.0 },
      { age: 19, eggsPerBird: 0.1 },
      { age: 20, eggsPerBird: 0.2 },
      { age: 21, eggsPerBird: 0.3 },
      { age: 22, eggsPerBird: 0.4 },
      { age: 23, eggsPerBird: 0.5 },
      { age: 24, eggsPerBird: 0.6 },
      { age: 25, eggsPerBird: 0.7 },
      { age: 26, eggsPerBird: 0.8 },
      { age: 27, eggsPerBird: 0.85 },
      { age: 28, eggsPerBird: 0.9 },
      { age: 29, eggsPerBird: 0.92 },
      { age: 30, eggsPerBird: 0.94 },
      { age: 32, eggsPerBird: 0.95 },
      { age: 34, eggsPerBird: 0.96 },
      { age: 36, eggsPerBird: 0.97 },
      { age: 38, eggsPerBird: 0.96 },
      { age: 40, eggsPerBird: 0.95 },
      { age: 42, eggsPerBird: 0.94 },
      { age: 44, eggsPerBird: 0.93 },
      { age: 46, eggsPerBird: 0.92 },
      { age: 48, eggsPerBird: 0.91 },
      { age: 50, eggsPerBird: 0.90 },
      { age: 52, eggsPerBird: 0.89 },
      { age: 54, eggsPerBird: 0.88 },
      { age: 56, eggsPerBird: 0.87 },
      { age: 58, eggsPerBird: 0.86 },
      { age: 60, eggsPerBird: 0.85 },
      { age: 62, eggsPerBird: 0.84 },
      { age: 64, eggsPerBird: 0.83 },
      { age: 66, eggsPerBird: 0.82 },
      { age: 68, eggsPerBird: 0.81 },
      { age: 70, eggsPerBird: 0.80 },
      { age: 72, eggsPerBird: 0.79 }
    ]
  },
  {
    breed: 'Hubbard',
    type: 'layer',
    weightTargets: [
      { age: 7, weight: 0.12 },
      { age: 14, weight: 0.25 },
      { age: 21, weight: 0.45 },
      { age: 28, weight: 0.70 },
      { age: 35, weight: 0.95 },
      { age: 42, weight: 1.20 },
      { age: 49, weight: 1.40 },
      { age: 56, weight: 1.55 },
      { age: 63, weight: 1.65 },
      { age: 70, weight: 1.70 }
    ],
    fcrTargets: [
      { age: 7, fcr: 1.1 },
      { age: 14, fcr: 1.3 },
      { age: 21, fcr: 1.5 },
      { age: 28, fcr: 1.7 },
      { age: 35, fcr: 1.9 },
      { age: 42, fcr: 2.1 },
      { age: 49, fcr: 2.3 },
      { age: 56, fcr: 2.5 },
      { age: 63, fcr: 2.7 },
      { age: 70, fcr: 2.9 }
    ],
    mortalityTargets: [
      { age: 7, mortality: 0.5 },
      { age: 14, mortality: 1.0 },
      { age: 21, mortality: 1.5 },
      { age: 28, mortality: 2.0 },
      { age: 35, mortality: 2.5 },
      { age: 42, mortality: 3.0 },
      { age: 49, mortality: 3.5 },
      { age: 56, mortality: 4.0 },
      { age: 63, mortality: 4.5 },
      { age: 70, mortality: 5.0 }
    ],
    eggProduction: [
      { age: 20, eggsPerBird: 0 },
      { age: 21, eggsPerBird: 0.1 },
      { age: 22, eggsPerBird: 0.2 },
      { age: 23, eggsPerBird: 0.3 },
      { age: 24, eggsPerBird: 0.4 },
      { age: 25, eggsPerBird: 0.5 },
      { age: 26, eggsPerBird: 0.6 },
      { age: 27, eggsPerBird: 0.7 },
      { age: 28, eggsPerBird: 0.8 },
      { age: 29, eggsPerBird: 0.85 },
      { age: 30, eggsPerBird: 0.9 },
      { age: 35, eggsPerBird: 0.95 },
      { age: 40, eggsPerBird: 0.9 },
      { age: 45, eggsPerBird: 0.85 },
      { age: 50, eggsPerBird: 0.8 },
      { age: 55, eggsPerBird: 0.75 },
      { age: 60, eggsPerBird: 0.7 },
      { age: 65, eggsPerBird: 0.65 },
      { age: 70, eggsPerBird: 0.6 }
    ]
  }
];


const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  background: #f8fafc;
  color: #2d3748;
  min-height: 100vh;
`;


const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;


const Button = styled.button`
  padding: 14px 28px;
  border-radius: 25px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  background: #2e7d32;
  color: white;
  margin-bottom: 30px;

  &:hover {
    background: #1b5e20;
    transform: translateY(-1px);
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const SalesSection = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d5016;
  margin: 0 0 20px 0;
`;






const Analytics: React.FC = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    try {
      const batchData = batchService.loadBatches();
      setBatches(batchData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };


  // Performance curves data
  const generatePerformanceCurves = () => {
    const performanceData = batches.map(batch => {
      const age = batch.age || 0;
      const weight = batch.currentWeight || 0;
      const targetWeight = batch.targetWeight || 0;
      const fcr = batch.averageFCR || 1.5;
      const mortality = batch.mortality || 0;
      const uniformity = batch.averageUniformity || 85;

      return {
        batchName: batch.name,
        age,
        weight,
        targetWeight,
        fcr,
        mortality,
        uniformity,
        performance: batch.averageWeight ? 
          (batch.averageWeight >= targetWeight * 0.95 ? 'excellent' : 
           batch.averageWeight >= targetWeight * 0.85 ? 'good' : 'fair') : 'unknown'
      };
    });

    return performanceData;
  };

  const performanceCurves = generatePerformanceCurves();


  // Production Analysis with Breed Standards
  const generateProductionAnalysis = () => {
    const analysis = batches.map(batch => {
      const breed = batch.breed || 'Unknown';
      const age = batch.age || 0;
      const currentWeight = batch.currentWeight || 0;
      const fcr = batch.averageFCR || 1.5;
      const mortality = batch.mortality || 0;
      
      // Find matching breed standard
      const standard = BREED_STANDARDS.find(s => 
        s.breed.toLowerCase().includes(breed.toLowerCase()) || 
        (s.type === 'broiler' && breed.toLowerCase().includes('broiler')) ||
        (s.type === 'layer' && breed.toLowerCase().includes('layer'))
      );
      
      if (!standard) {
        return {
          batchName: batch.name,
          breed: breed,
          age: age,
          currentWeight: currentWeight,
          targetWeight: 0,
          weightPerformance: 'unknown',
          fcr: fcr,
          targetFcr: 0,
          fcrPerformance: 'unknown',
          mortality: mortality,
          targetMortality: 0,
          mortalityPerformance: 'unknown',
          overallPerformance: 'unknown'
        };
      }
      
      // Find target values for current age
      const weightTarget = standard.weightTargets.find(w => w.age >= age)?.weight || 
                          standard.weightTargets[standard.weightTargets.length - 1]?.weight || 0;
      const fcrTarget = standard.fcrTargets.find(f => f.age >= age)?.fcr || 
                       standard.fcrTargets[standard.fcrTargets.length - 1]?.fcr || 0;
      const mortalityTarget = standard.mortalityTargets.find(m => m.age >= age)?.mortality || 
                             standard.mortalityTargets[standard.mortalityTargets.length - 1]?.mortality || 0;
      
      // Calculate performance
      const weightPerformance = currentWeight >= weightTarget * 0.95 ? 'excellent' :
                               currentWeight >= weightTarget * 0.85 ? 'good' : 'poor';
      const fcrPerformance = fcr <= fcrTarget * 1.1 ? 'excellent' :
                            fcr <= fcrTarget * 1.2 ? 'good' : 'poor';
      const mortalityPerformance = mortality <= mortalityTarget * 1.1 ? 'excellent' :
                                  mortality <= mortalityTarget * 1.2 ? 'good' : 'poor';
      
      // Overall performance
      const performances = [weightPerformance, fcrPerformance, mortalityPerformance];
      const excellentCount = performances.filter(p => p === 'excellent').length;
      const goodCount = performances.filter(p => p === 'good').length;
      
      const overallPerformance = excellentCount >= 2 ? 'excellent' :
                                (excellentCount + goodCount) >= 2 ? 'good' : 'poor';
      
      return {
        batchName: batch.name,
        breed: breed,
        standard: standard.breed,
        age: age,
        currentWeight: currentWeight,
        targetWeight: weightTarget,
        weightPerformance: weightPerformance,
        fcr: fcr,
        targetFcr: fcrTarget,
        fcrPerformance: fcrPerformance,
        mortality: mortality,
        targetMortality: mortalityTarget,
        mortalityPerformance: mortalityPerformance,
        overallPerformance: overallPerformance
      };
    });
    
    return analysis;
  };

  const productionAnalysis = generateProductionAnalysis();

  if (loading) {
    return (
        <Container>
          <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading production data...</div>
          </div>
        </Container>
    );
  }

  return (
    <>
      <Container>


        {/* Performance Curves Section */}
        <SalesSection>
          <SectionTitle>Performance Curves</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {performanceCurves.map((curve, index) => (
              <StatCard key={index}>
                <StatHeader>
                  <StatIcon color={curve.performance === 'excellent' ? '#4caf50' : curve.performance === 'good' ? '#ff9800' : '#f44336'}>
                    <BarChart size={24} />
                  </StatIcon>
                  <StatTitle>{curve.batchName}</StatTitle>
                </StatHeader>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Age</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{curve.age} days</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Weight</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{curve.weight} kg</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>FCR</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{curve.fcr}</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Mortality</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{curve.mortality}%</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Uniformity</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{curve.uniformity}%</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Performance</div>
                    <div style={{ 
                      fontWeight: '600', 
                      color: curve.performance === 'excellent' ? '#4caf50' : curve.performance === 'good' ? '#ff9800' : '#f44336',
                      textTransform: 'capitalize'
                    }}>
                      {curve.performance}
                    </div>
                  </div>
                </div>
              </StatCard>
            ))}
          </div>
        </SalesSection>


        {/* Production Analysis Section */}
        <SalesSection>
          <SectionTitle>Production Analysis vs Breed Standards</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
            {productionAnalysis.map((analysis, index) => (
              <StatCard key={index}>
            <StatHeader>
                  <StatIcon color={analysis.overallPerformance === 'excellent' ? '#4caf50' : analysis.overallPerformance === 'good' ? '#ff9800' : '#f44336'}>
                    <Activity size={24} />
              </StatIcon>
                  <StatTitle>{analysis.batchName}</StatTitle>
            </StatHeader>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                    Breed: {analysis.breed} | Standard: {analysis.standard} | Age: {analysis.age} days
                  </div>
                  <div style={{ 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    backgroundColor: analysis.overallPerformance === 'excellent' ? '#e8f5e8' : 
                                   analysis.overallPerformance === 'good' ? '#fff3e0' : '#ffebee',
                    color: analysis.overallPerformance === 'excellent' ? '#2e7d32' : 
                           analysis.overallPerformance === 'good' ? '#f57c00' : '#d32f2f',
                    display: 'inline-block'
                  }}>
                    Overall: {analysis.overallPerformance}
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Weight Performance</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {analysis.currentWeight}kg / {analysis.targetWeight}kg
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem',
                      color: analysis.weightPerformance === 'excellent' ? '#4caf50' : 
                             analysis.weightPerformance === 'good' ? '#ff9800' : '#f44336',
                      textTransform: 'capitalize'
                    }}>
                      {analysis.weightPerformance}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>FCR Performance</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {analysis.fcr} / {analysis.targetFcr}
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem',
                      color: analysis.fcrPerformance === 'excellent' ? '#4caf50' : 
                             analysis.fcrPerformance === 'good' ? '#ff9800' : '#f44336',
                      textTransform: 'capitalize'
                    }}>
                      {analysis.fcrPerformance}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Mortality Performance</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {analysis.mortality}% / {analysis.targetMortality}%
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem',
                      color: analysis.mortalityPerformance === 'excellent' ? '#4caf50' : 
                             analysis.mortalityPerformance === 'good' ? '#ff9800' : '#f44336',
                      textTransform: 'capitalize'
                    }}>
                      {analysis.mortalityPerformance}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>Breed Standard</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {analysis.standard}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      {analysis.standard === 'COBB 500' ? 'Broiler Standard' : 'Layer Standard'}
                    </div>
                  </div>
                </div>
          </StatCard>
            ))}
          </div>
        </SalesSection>

        {/* Growth Curves Section */}
        <SalesSection>
          <SectionTitle>Growth Curves vs Breed Standards</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
            {batches.map((batch, index) => {
              const breed = batch.breed || 'Unknown';
              const standard = BREED_STANDARDS.find(s => 
                s.breed.toLowerCase().includes(breed.toLowerCase()) || 
                (s.type === 'broiler' && breed.toLowerCase().includes('broiler')) ||
                (s.type === 'layer' && breed.toLowerCase().includes('layer'))
              );
              
              if (!standard) return null;
              
              return (
                <StatCard key={index}>
            <StatHeader>
                    <StatIcon color="#2196f3">
                <BarChart3 size={24} />
              </StatIcon>
                    <StatTitle>{batch.name} - {standard.breed}</StatTitle>
            </StatHeader>
                  
                  {/* Weight Growth Curve */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '1rem' }}>Weight Growth Curve</h4>
                    <div style={{ 
                      height: '200px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      padding: '16px',
                      position: 'relative',
                      border: '1px solid #e0e0e0'
                    }}>
                      {/* Simple visual representation of growth curve */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'end', 
                        height: '100%', 
                        gap: '8px',
                        justifyContent: 'space-between'
                      }}>
                        {standard.weightTargets.slice(0, 6).map((target, i) => {
                          const currentAge = batch.age || 0;
                          const isCurrentAge = target.age <= currentAge;
                          const height = (target.weight / Math.max(...standard.weightTargets.map(t => t.weight))) * 120;
                          
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                              <div style={{ 
                                width: '100%',
                                height: `${height}px`,
                                backgroundColor: isCurrentAge ? '#4caf50' : '#e0e0e0',
                                borderRadius: '4px 4px 0 0',
                                marginBottom: '8px',
                                position: 'relative'
                              }}>
                                <div style={{ 
                                  position: 'absolute',
                                  top: '-20px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  fontSize: '0.7rem',
                                  color: '#666',
                                  fontWeight: '500'
                                }}>
                                  {target.weight}kg
                                </div>
                              </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#666',
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>
                                {target.age}d
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ 
                        position: 'absolute',
                        bottom: '8px',
                        left: '16px',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}>
                        Current: {batch.currentWeight || 0}kg at {batch.age || 0} days
                      </div>
                    </div>
                  </div>
                  
                  {/* FCR Trend */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '1rem' }}>Feed Conversion Ratio (FCR)</h4>
                    <div style={{ 
                      height: '120px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      padding: '16px',
                      position: 'relative',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'end', 
                        height: '100%', 
                        gap: '8px',
                        justifyContent: 'space-between'
                      }}>
                        {standard.fcrTargets.slice(0, 6).map((target, i) => {
                          const currentAge = batch.age || 0;
                          const isCurrentAge = target.age <= currentAge;
                          const height = (target.fcr / Math.max(...standard.fcrTargets.map(t => t.fcr))) * 80;
                          
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                              <div style={{ 
                                width: '100%',
                                height: `${height}px`,
                                backgroundColor: isCurrentAge ? '#ff9800' : '#e0e0e0',
                                borderRadius: '4px 4px 0 0',
                                marginBottom: '8px',
                                position: 'relative'
                              }}>
                                <div style={{ 
                                  position: 'absolute',
                                  top: '-20px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  fontSize: '0.7rem',
                                  color: '#666',
                                  fontWeight: '500'
                                }}>
                                  {target.fcr}
                                </div>
                              </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#666',
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>
                                {target.age}d
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ 
                        position: 'absolute',
                        bottom: '8px',
                        left: '16px',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}>
                        Current: {batch.averageFCR || 1.5} at {batch.age || 0} days
                      </div>
                    </div>
                  </div>
                  
                  {/* Performance Summary */}
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: '8px',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ fontWeight: '600', color: '#2e7d32', marginBottom: '8px' }}>Performance Summary</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.8rem' }}>
                      <div>Weight: {((batch.currentWeight || 0) / (standard.weightTargets.find(w => w.age >= (batch.age || 0))?.weight || 1) * 100).toFixed(1)}% of target</div>
                      <div>FCR: {((batch.averageFCR || 1.5) / (standard.fcrTargets.find(f => f.age >= (batch.age || 0))?.fcr || 1.5) * 100).toFixed(1)}% of target</div>
                      <div>Mortality: {batch.mortality || 0}% (Target: {standard.mortalityTargets.find(m => m.age >= (batch.age || 0))?.mortality || 0}%)</div>
                      <div>Uniformity: {batch.averageUniformity || 85}%</div>
                    </div>
                  </div>
          </StatCard>
              );
            })}
          </div>
        </SalesSection>



        <ActionBar>
          <Button onClick={loadData}>
            <TrendingUp size={20} />
            Refresh Production Data
          </Button>
          <Button>
            <Download size={20} />
            Export Production Report
          </Button>
        </ActionBar>

      </Container>
    </>
  );
};

export default Analytics;
